import { GROUP_BY, GROUP_BY_OPTIONS, getDueBucketLabel } from "./taskGrouping";
import { getTaskPriorityLabel } from "./taskPriority";

/**
 * Views = filtro + agrupamento + modo, com nome.
 *
 * A ideia que organiza a central: cada recorte de tarefa ("minhas", "hoje",
 * "atrasadas") e uma view, nao uma pagina. Sem isso, todo recorte novo vira
 * mais uma tela no menu - que foi exatamente o que aconteceu antes.
 *
 * As views padrao vivem no codigo. As customizadas vivem no banco e podem ser
 * compartilhadas com o workspace.
 */

export const VIEW_MODE = { LISTA: "lista", QUADRO: "quadro" };

export const COMPARTILHAMENTO = { PRIVADA: 0, WORKSPACE: 1 };

// Sentinela resolvida na hora de filtrar, quando o id do usuario e conhecido.
// E o que faz uma view compartilhada mostrar as tarefas de QUEM esta olhando,
// em vez das tarefas de quem criou a view.
export const RESPONSAVEL_EU = "me";
export const RESPONSAVEL_SEM_DONO = "sem-dono";

export const FILTROS_VAZIOS = {
  busca: "",
  projeto: "all",
  responsavel: "all",
  prazo: "all",
  prioridade: "all",
  status: "all",
  tag: "all",
  customFields: {},
};

function view(id, nome, filtros, extras = {}) {
  return {
    id,
    nome,
    sistema: true,
    minha: true,
    compartilhamento: COMPARTILHAMENTO.PRIVADA,
    autor: null,
    filtros: { ...FILTROS_VAZIOS, ...filtros },
    agruparPor: extras.agruparPor || GROUP_BY.PRAZO,
    modo: extras.modo || VIEW_MODE.LISTA,
    ocultarConcluidas: extras.ocultarConcluidas !== false,
  };
}

export const VIEWS_PADRAO = [
  view("minhas", "Minhas", { responsavel: RESPONSAVEL_EU }),
  view("hoje", "Hoje", { responsavel: RESPONSAVEL_EU, prazo: "hoje" }, {
    agruparPor: GROUP_BY.PRIORIDADE,
  }),
  view("atrasadas", "Atrasadas", { prazo: "atrasada" }, {
    agruparPor: GROUP_BY.RESPONSAVEL,
  }),
  view("time", "Do time", {}, { agruparPor: GROUP_BY.RESPONSAVEL }),
  view("sem-dono", "Sem responsavel", { responsavel: RESPONSAVEL_SEM_DONO }, {
    agruparPor: GROUP_BY.PROJETO,
  }),
  view("todas", "Todas", {}, { agruparPor: GROUP_BY.NENHUM, ocultarConcluidas: false }),
];

export const VIEW_PADRAO_ID = "minhas";

/** Converte o registro do servidor para a forma usada na tela. */
export function normalizarViewDoServidor(registro) {
  return {
    id: registro.id,
    nome: registro.nome,
    sistema: false,
    minha: Boolean(registro.minha),
    compartilhamento: Number(registro.compartilhamento) || COMPARTILHAMENTO.PRIVADA,
    autor: registro.autor || null,
    filtros: { ...FILTROS_VAZIOS, ...(registro.filtros || {}) },
    agruparPor: registro.agruparPor || GROUP_BY.PRAZO,
    modo: registro.modo || VIEW_MODE.LISTA,
    ocultarConcluidas: registro.ocultarConcluidas !== false,
  };
}

/** Payload aceito pelo backend. */
export function paraPayload(view) {
  return {
    nome: view.nome,
    filtros: view.filtros,
    agruparPor: view.agruparPor,
    modo: view.modo,
    ocultarConcluidas: view.ocultarConcluidas,
    compartilhamento: view.compartilhamento,
  };
}

/**
 * A view se adapta a quem esta olhando?
 *
 * Uma view compartilhada com responsavel = "eu" mostra, para cada pessoa do
 * time, as tarefas dela. Uma view com responsavel = joao mostra as do Joao
 * para todo mundo. A diferenca importa e por isso ela e sinalizada na tela.
 */
export function adaptaAoLeitor(view) {
  return view?.filtros?.responsavel === RESPONSAVEL_EU;
}

export function ehCompartilhada(view) {
  return Number(view?.compartilhamento) === COMPARTILHAMENTO.WORKSPACE;
}

/**
 * Descricao legivel do que a view faz.
 *
 * Sem isto, uma aba compartilhada e so um nome: ninguem sabe o que ela filtra
 * sem abrir e conferir campo por campo.
 */
export function descreverView(view, contexto = {}) {
  if (!view) return "";

  const { filtros = {}, agruparPor, ocultarConcluidas } = view;
  const partes = [];

  const nomeDe = (lista, valor) =>
    (lista || []).find((item) => String(item.value) === String(valor))?.label || null;

  if (filtros.responsavel === RESPONSAVEL_EU) {
    partes.push("de quem esta vendo");
  } else if (filtros.responsavel === RESPONSAVEL_SEM_DONO) {
    partes.push("sem responsavel");
  } else if (filtros.responsavel && filtros.responsavel !== "all") {
    partes.push(nomeDe(contexto.responsaveis, filtros.responsavel) || "responsavel especifico");
  }

  if (filtros.projeto && filtros.projeto !== "all") {
    partes.push(nomeDe(contexto.projetos, filtros.projeto) || "um projeto");
  }

  if (filtros.prazo && filtros.prazo !== "all") {
    partes.push(getDueBucketLabel(filtros.prazo).toLowerCase());
  }

  if (filtros.prioridade && filtros.prioridade !== "all") {
    partes.push(`prioridade ${getTaskPriorityLabel(filtros.prioridade).toLowerCase()}`);
  }

  if (filtros.status && filtros.status !== "all") {
    partes.push(filtros.status === "concluida" ? "concluidas" : "pendentes");
  }

  if (filtros.tag && filtros.tag !== "all") {
    partes.push(nomeDe(contexto.tags, filtros.tag) || "com tag");
  }

  if (filtros.busca) {
    partes.push(`contendo "${filtros.busca}"`);
  }

  if (!partes.length) partes.push("todas as tarefas");

  if (agruparPor && agruparPor !== GROUP_BY.NENHUM) {
    const rotulo = GROUP_BY_OPTIONS.find((item) => item.value === agruparPor)?.label;
    if (rotulo) partes.push(`agrupado por ${rotulo.toLowerCase()}`);
  }

  if (ocultarConcluidas === false) partes.push("incluindo concluidas");

  return partes.join(" · ");
}

/** Compara o estado atual com a view escolhida, para sinalizar "nao salvo". */
export function viewFoiAlterada(view, estado) {
  if (!view) return false;

  const mesmoFiltro =
    JSON.stringify({ ...FILTROS_VAZIOS, ...view.filtros }) ===
    JSON.stringify({ ...FILTROS_VAZIOS, ...estado.filtros });

  return (
    !mesmoFiltro ||
    view.agruparPor !== estado.agruparPor ||
    view.modo !== estado.modo ||
    view.ocultarConcluidas !== estado.ocultarConcluidas
  );
}

// ─── Preferencia pessoal de exibicao ─────────────────────────────────────────
// Quem compartilha nao decide o que aparece na aba de quem le. Cada um esconde
// as views do time que nao usa - e o que evita a barra virar um lixao quando o
// time tem 30 views.

const chaveOcultas = (workspaceId) => `levelup:views-ocultas:${workspaceId || "sem-workspace"}`;

export function carregarViewsOcultas(workspaceId) {
  try {
    const bruto = localStorage.getItem(chaveOcultas(workspaceId));
    const lista = bruto ? JSON.parse(bruto) : [];
    return Array.isArray(lista) ? lista.map(String) : [];
  } catch (error) {
    return [];
  }
}

export function alternarViewOculta(workspaceId, viewId) {
  const atuais = carregarViewsOcultas(workspaceId);
  const id = String(viewId);
  const novas = atuais.includes(id)
    ? atuais.filter((item) => item !== id)
    : [...atuais, id];

  try {
    localStorage.setItem(chaveOcultas(workspaceId), JSON.stringify(novas));
  } catch (error) {
    console.error("taskViews: erro ao gravar preferencia de exibicao", error);
  }

  return novas;
}

// ─── Migracao das views que ficaram no localStorage ──────────────────────────

const chaveLegado = (workspaceId) => `levelup:views-tarefas:${workspaceId || "sem-workspace"}`;

/**
 * Recupera views criadas antes de existir persistencia no banco e limpa a
 * chave antiga. Roda uma vez por workspace, em silencio.
 */
export function coletarViewsLegadas(workspaceId) {
  try {
    const bruto = localStorage.getItem(chaveLegado(workspaceId));
    if (!bruto) return [];

    const lista = JSON.parse(bruto);
    localStorage.removeItem(chaveLegado(workspaceId));

    return Array.isArray(lista) ? lista : [];
  } catch (error) {
    return [];
  }
}
